import { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { Panel, PanelHeader, PanelBody, PageHeader } from '../components/ui/Panel';
import { PhaseChip } from '../components/ui/Pill';
import Button from '../components/ui/Button';
import { LoadingPanel } from '../components/ui/States';
import { fmt, DASH } from '../lib/format';
import { canMutate } from '../lib/roles';
import { useAuth } from '../contexts/AuthContext';

const SECTIONS = [
  {
    key: 'membership',
    label: 'Membership & growth',
    note: 'Totals, new registrations, retention by cluster',
    on: true,
    phase3: false,
  },
  {
    key: 'demographics',
    label: 'Demographics & equity',
    note: 'All dimensions, exact counts',
    on: true,
    phase3: false,
  },
  {
    key: 'training',
    label: 'Training engagement',
    note: 'Moodle completions and event attendance',
    on: true,
    phase3: false,
  },
  {
    key: 'mentorship',
    label: 'Mentorship program',
    note: 'Matches, sessions, feedback — Phase 3 data',
    on: false,
    phase3: true,
  },
];

function fiscalYears() {
  const now = new Date();
  const startYear = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
  return [0, 1, 2].map((i) => {
    const y = startYear - i;
    return { key: `${y}`, label: `${y}–${String(y + 1).slice(2)}` };
  });
}

export default function Reports() {
  const { user } = useAuth();
  const mutable = canMutate(user?.role);
  const years = fiscalYears();

  const [year, setYear] = useState(years[0].key);
  const [sections, setSections] = useState<string[]>(SECTIONS.filter((s) => s.on).map((s) => s.key));

  const { data, isLoading } = useQuery('report-headline', async () => {
    const res = await api.get('/analytics/dashboard');
    return res.data.data as {
      kpis: { totalMembers: number; registrations30: number; attendance30: number };
    };
  });

  const toggle = (key: string) => setSections((s) => (s.includes(key) ? s.filter((x) => x !== key) : [...s, key]));

  return (
    <>
      <PageHeader
        title="Annual funder report"
        eyebrow={<PhaseChip label="Phase 3" />}
        sub="One fixed template, generated from live data at the moment you press generate."
      />

      <Panel className="mb-6">
        <PanelHeader title="CIHR HRTP annual report tables">
          <Link to="/reports/cihr" className="text-small underline">
            Open
          </Link>
        </PanelHeader>
        <PanelBody>
          <p className="text-table text-ink-2">
            Tables 1a–1c and 2 for a calendar year from platform data, each number with how it was counted; fills
            CIHR&apos;s own template and freezes snapshots.
          </p>
        </PanelBody>
      </Panel>

      <div className="grid gap-6 items-start grid-cols-2">
        <Panel>
          <PanelHeader title="Configuration" />
          <PanelBody>
            <label className="font-semibold block text-small text-ink-2 mb-2">Reporting year — April to March</label>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="border border-line-strong bg-white px-3 py-[9px] text-table min-w-[220px]"
            >
              {years.map((y) => (
                <option key={y.key} value={y.key}>
                  {y.label}
                </option>
              ))}
            </select>

            <div className="mt-6">
              {SECTIONS.map((s) => (
                <label
                  key={s.key}
                  className="flex items-start gap-3 py-[12px] border-b border-line last:border-b-0 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={sections.includes(s.key)}
                    onChange={() => toggle(s.key)}
                    disabled={s.phase3}
                    className="w-[15px] h-[15px] accent-accent mt-[2px] disabled:opacity-45"
                  />
                  <span>
                    <span className="block text-table font-semibold">{s.label}</span>
                    <span className="block text-small text-ink-2 mt-[2px]">{s.note}</span>
                  </span>
                </label>
              ))}
            </div>

            <div className="flex gap-3 mt-6">
              <Button variant="primary" disabled={!mutable}>
                Generate PDF
              </Button>
              <Button variant="outline" disabled={!mutable}>
                Generate XLSX
              </Button>
            </div>

            <p className="text-small text-ink-2 mt-4">
              Generation is not wired up yet — the export pipeline lands with Phase 3. Everything above reflects the
              agreed template.
            </p>
          </PanelBody>
        </Panel>

        <div className="flex flex-col gap-6">
          <Panel>
            <PanelHeader title="Headline figures" note="Live · recomputed at generation" />
            {isLoading || !data ? (
              <LoadingPanel label="Loading figures…" height={220} />
            ) : (
              <div className="grid grid-cols-2 gap-px bg-line">
                {[
                  {
                    label: 'Members at year end',
                    value: fmt(data.kpis.totalMembers),
                    note: 'All registered members',
                  },
                  {
                    label: 'New this year',
                    value: DASH,
                    note: 'Needs the fiscal-year query — Phase 3',
                  },
                  {
                    label: 'Course completions',
                    value: DASH,
                    note: 'Needs the fiscal-year query — Phase 3',
                  },
                  {
                    label: 'Event attendances · 30d',
                    value: fmt(data.kpis.attendance30),
                    note: 'Rolling 30 days, not the fiscal year',
                  },
                ].map((c) => (
                  <div key={c.label} className="bg-white px-5 py-4">
                    <div className="font-semibold tabular text-[42px] leading-none">{c.value}</div>
                    <div className="font-semibold text-small text-ink-2 mt-3">{c.label}</div>
                    <div className="text-small text-ink-2 mt-1">{c.note}</div>
                  </div>
                ))}
              </div>
            )}
          </Panel>
        </div>
      </div>
    </>
  );
}
